export type ScreenEdge = 'top' | 'right' | 'bottom' | 'left'

export interface ScreenPlacement {
  edge: ScreenEdge
  xPercent: number
  yPercent: number
  arrowDegrees: number
}

const SAFE_X = 0.6
const SAFE_Y = 0.72
const MIN_LABEL_SEPARATION = 9

export function shouldShowOffscreenIndicators(paused: boolean, phase: RacePhase): boolean {
  return !paused && phase !== 'finished'
}

export function getViewportCenter(size: { width: number; height: number }): [number, number] {
  return [size.width / 2, size.height / 2]
}

interface Point3 {
  x: number
  y: number
  z: number
}

export function getOverlayAnchor(camera: Point3, direction: Point3): Point3 {
  return {
    x: camera.x + direction.x,
    y: camera.y + direction.y,
    z: camera.z + direction.z,
  }
}

export function placeOffscreenPoint(input: { x: number; y: number; behind: boolean }): ScreenPlacement | null {
  if (!input.behind && Math.abs(input.x) <= SAFE_X && Math.abs(input.y) <= SAFE_Y) return null

  const x = input.behind ? -input.x : input.x
  let y = input.behind ? -input.y : input.y
  if (Math.hypot(x, y) < 1e-6) y = -1
  const horizontalScale = Math.abs(x) > 1e-6 ? SAFE_X / Math.abs(x) : Number.POSITIVE_INFINITY
  const verticalScale = Math.abs(y) > 1e-6 ? SAFE_Y / Math.abs(y) : Number.POSITIVE_INFINITY
  const scale = Math.min(horizontalScale, verticalScale)
  const edgeX = x * scale
  const edgeY = y * scale
  const edge: ScreenEdge = horizontalScale < verticalScale ? (x > 0 ? 'right' : 'left') : y > 0 ? 'top' : 'bottom'

  return {
    edge,
    xPercent: 50 + edgeX * 50,
    yPercent: 50 - edgeY * 50,
    arrowDegrees: (Math.atan2(x, y) * 180) / Math.PI,
  }
}

export function spreadEdgeMarkers<T extends ScreenPlacement>(markers: readonly T[]): T[] {
  const arranged = markers.map((marker) => ({ ...marker }))
  for (const edge of ['top', 'right', 'bottom', 'left'] as const) {
    const vertical = edge === 'right' || edge === 'left'
    const grouped = arranged
      .map((marker, index) => ({ index, marker }))
      .filter(({ marker }) => marker.edge === edge)
      .sort((a, b) => {
        const first = vertical ? a.marker.yPercent : a.marker.xPercent
        const second = vertical ? b.marker.yPercent : b.marker.xPercent
        return first - second
      })
    if (grouped.length < 2) continue

    const minimum = vertical ? 14 : 12
    const maximum = vertical ? 72 : 88
    const coordinates = grouped.map(({ marker }) => (vertical ? marker.yPercent : marker.xPercent))
    coordinates[0] = Math.max(minimum, coordinates[0] ?? minimum)
    for (let index = 1; index < coordinates.length; index += 1) {
      coordinates[index] = Math.max(
        coordinates[index] ?? minimum,
        (coordinates[index - 1] ?? minimum) + MIN_LABEL_SEPARATION,
      )
    }
    const overflow = Math.max(0, (coordinates.at(-1) ?? maximum) - maximum)
    for (let index = 0; index < coordinates.length; index += 1)
      coordinates[index] = (coordinates[index] ?? 0) - overflow
    const underflow = Math.max(0, minimum - (coordinates[0] ?? minimum))
    for (let index = 0; index < coordinates.length; index += 1)
      coordinates[index] = (coordinates[index] ?? 0) + underflow

    grouped.forEach(({ index }, groupIndex) => {
      const coordinate = coordinates[groupIndex] ?? 50
      const marker = arranged[index]
      if (!marker) return
      arranged[index] = {
        ...marker,
        xPercent: vertical ? marker.xPercent : coordinate,
        yPercent: vertical ? coordinate : marker.yPercent,
      }
    })
  }
  return arranged
}

import type { RacePhase } from '../game/types'
